/**
 * Wire protocol. Every client -> server payload is validated at runtime with
 * the schemas below before it reaches the engine; nothing trusts the client.
 *
 * Every mutating action carries `actionId` (idempotency key) and
 * `expectedPhaseId`. The server answers `duplicate` for a replayed actionId
 * and `stale-phase` when the phase moved on while the packet was in flight,
 * which is what makes double-submits and late votes harmless.
 */

import { z } from 'zod';
import {
  CONNECTION_ROLES,
  CONSEQUENCE_MAX_LENGTH,
  LOCALES,
  NICKNAME_MAX_LENGTH,
  ROOM_CODE_LENGTH,
  ROOM_NAME_MAX_LENGTH,
  WISH_MAX_LENGTH,
} from './constants.js';

export const roomCodeSchema = z
  .string()
  .trim()
  .toUpperCase()
  .length(ROOM_CODE_LENGTH)
  .regex(/^[A-Z]+$/);

const codePointLimitedString = (maxLength: number) => z.string().refine(
  (value) => Array.from(value).length <= maxLength,
  'too_big',
);

export const nicknameSchema = z
  .string()
  .transform((s) => s.trim())
  .pipe(z.string().min(1).and(codePointLimitedString(NICKNAME_MAX_LENGTH)));

export const actionEnvelope = {
  actionId: z.string().min(8).max(64),
  expectedPhaseId: z.string().min(1).max(64),
};

export const settingsPatchSchema = z
  .object({
    roomName: codePointLimitedString(ROOM_NAME_MAX_LENGTH),
    locale: z.enum(LOCALES),
    rounds: z.number().int().min(1).max(5),
    wishSeconds: z.number().int().min(10).max(120),
    consequenceSeconds: z.number().int().min(10).max(90),
    voteSeconds: z.number().int().min(5).max(60),
    revealSecondsPerWish: z.number().int().min(3).max(20),
    hellMode: z.boolean(),
    familyFilter: z.boolean(),
    autoReveal: z.boolean(),
    sharedDisplay: z.boolean(),
  })
  .partial();

// --- client -> server ------------------------------------------------------

export const roomCreateSchema = z.object({
  ...actionEnvelope,
  nickname: nicknameSchema,
  avatar: codePointLimitedString(8).optional(),
  role: z.enum(CONNECTION_ROLES).default('player'),
  platform: z.string().max(32).default('web'),
  locale: z.enum(LOCALES).default('pt-PT'),
});

export const roomJoinSchema = z.object({
  ...actionEnvelope,
  code: roomCodeSchema,
  nickname: nicknameSchema,
  avatar: codePointLimitedString(8).optional(),
  role: z.enum(CONNECTION_ROLES).default('player'),
  platform: z.string().max(32).default('web'),
});

export const roomReconnectSchema = z.object({
  ...actionEnvelope,
  code: roomCodeSchema,
  participantId: z.string().min(1).max(64),
  reconnectToken: z.string().min(16).max(256),
  platform: z.string().max(32).default('web'),
});

export const controllerIssuePairingSchema = z.object({ ...actionEnvelope });
export const controllerPairSchema = z.object({
  ...actionEnvelope,
  code: roomCodeSchema,
  pairingToken: z.string().min(24).max(256),
  platform: z.string().max(32).default('web'),
});
export const controllerUnpairSchema = z.object({ ...actionEnvelope });
export const demoCreateSchema = z.object({
  ...actionEnvelope,
  speed: z.number().int().min(1).max(1_000).default(1),
  platform: z.string().max(32).default('web'),
});

export const roomReadySchema = z.object({ ...actionEnvelope, ready: z.boolean() });

export const roomUpdateSettingsSchema = z.object({
  ...actionEnvelope,
  patch: settingsPatchSchema,
});

export const roomAddBotSchema = z.object({ ...actionEnvelope });
export const roomRemoveBotSchema = z.object({ ...actionEnvelope, participantId: z.string().min(1) });
export const roomKickSchema = z.object({ ...actionEnvelope, participantId: z.string().min(1) });
export const roomTransferHostSchema = z.object({
  ...actionEnvelope,
  participantId: z.string().min(1),
});

export const gameStartSchema = z.object({ ...actionEnvelope });

/**
 * A wish is one line of text. No caveats, no conditions, no second field:
 * the player asks for one thing and then loses control of it.
 */
export const wishSubmitSchema = z.object({
  ...actionEnvelope,
  text: codePointLimitedString(WISH_MAX_LENGTH),
});

export const wishDraftSchema = z.object({
  ...actionEnvelope,
  text: codePointLimitedString(WISH_MAX_LENGTH),
});

export const consequenceSubmitSchema = z.object({
  ...actionEnvelope,
  wishId: z.string().min(1).max(64),
  text: codePointLimitedString(CONSEQUENCE_MAX_LENGTH),
});

export const consequenceDraftSchema = z.object({
  ...actionEnvelope,
  wishId: z.string().min(1).max(64),
  text: codePointLimitedString(CONSEQUENCE_MAX_LENGTH),
});

export const voteSubmitSchema = z.object({
  ...actionEnvelope,
  wishId: z.string().min(1).max(64),
  consequenceId: z.string().min(1).max(64),
});

export const hostActionSchema = z.object({ ...actionEnvelope });
export const hostAddTimeSchema = z.object({
  ...actionEnvelope,
  seconds: z.number().int().min(5).max(60),
});

export const CLIENT_EVENTS = {
  roomCreate: 'room:create',
  roomJoin: 'room:join',
  roomReconnect: 'room:reconnect',
  roomLeave: 'room:leave',
  roomReady: 'room:ready',
  roomUpdateSettings: 'room:update-settings',
  roomAddBot: 'room:add-bot',
  roomRemoveBot: 'room:remove-bot',
  roomKick: 'room:kick',
  roomTransferHost: 'room:transfer-host',
  controllerIssuePairing: 'controller:issue-pairing',
  controllerPair: 'controller:pair',
  controllerUnpair: 'controller:unpair',
  demoCreate: 'demo:create',
  gameStart: 'game:start',
  wishUpdateDraft: 'wish:update-draft',
  wishSubmit: 'wish:submit',
  consequenceUpdateDraft: 'consequence:update-draft',
  consequenceSubmit: 'consequence:submit',
  voteSubmit: 'vote:submit',
  hostPause: 'host:pause',
  hostResume: 'host:resume',
  hostAddTime: 'host:add-time',
  hostAdvanceReveal: 'host:advance-reveal',
  gameNextRound: 'game:next-round',
  gameEnd: 'game:end',
} as const;

export const SERVER_EVENTS = {
  sessionCreated: 'session:created',
  roomSnapshot: 'room:snapshot',
  playerJoined: 'room:player-joined',
  playerLeft: 'room:player-left',
  playerStatus: 'room:player-status',
  phaseChanged: 'phase:changed',
  timerSync: 'timer:sync',
  submissionAccepted: 'submission:accepted',
  submissionRejected: 'submission:rejected',
  voteAccepted: 'vote:accepted',
  voteRejected: 'vote:rejected',
  revealSnapshot: 'reveal:snapshot',
  scoreboardSnapshot: 'scoreboard:snapshot',
  gameFinished: 'game:finished',
  publicError: 'error:public',
} as const;

export type ClientEventName = (typeof CLIENT_EVENTS)[keyof typeof CLIENT_EVENTS];
export type ServerEventName = (typeof SERVER_EVENTS)[keyof typeof SERVER_EVENTS];

export const CLIENT_PAYLOAD_SCHEMAS = {
  [CLIENT_EVENTS.roomCreate]: roomCreateSchema,
  [CLIENT_EVENTS.roomJoin]: roomJoinSchema,
  [CLIENT_EVENTS.roomReconnect]: roomReconnectSchema,
  [CLIENT_EVENTS.roomLeave]: z.object({ ...actionEnvelope }),
  [CLIENT_EVENTS.roomReady]: roomReadySchema,
  [CLIENT_EVENTS.roomUpdateSettings]: roomUpdateSettingsSchema,
  [CLIENT_EVENTS.roomAddBot]: roomAddBotSchema,
  [CLIENT_EVENTS.roomRemoveBot]: roomRemoveBotSchema,
  [CLIENT_EVENTS.roomKick]: roomKickSchema,
  [CLIENT_EVENTS.roomTransferHost]: roomTransferHostSchema,
  [CLIENT_EVENTS.controllerIssuePairing]: controllerIssuePairingSchema,
  [CLIENT_EVENTS.controllerPair]: controllerPairSchema,
  [CLIENT_EVENTS.controllerUnpair]: controllerUnpairSchema,
  [CLIENT_EVENTS.demoCreate]: demoCreateSchema,
  [CLIENT_EVENTS.gameStart]: gameStartSchema,
  [CLIENT_EVENTS.wishUpdateDraft]: wishDraftSchema,
  [CLIENT_EVENTS.wishSubmit]: wishSubmitSchema,
  [CLIENT_EVENTS.consequenceUpdateDraft]: consequenceDraftSchema,
  [CLIENT_EVENTS.consequenceSubmit]: consequenceSubmitSchema,
  [CLIENT_EVENTS.voteSubmit]: voteSubmitSchema,
  [CLIENT_EVENTS.hostPause]: hostActionSchema,
  [CLIENT_EVENTS.hostResume]: hostActionSchema,
  [CLIENT_EVENTS.hostAddTime]: hostAddTimeSchema,
  [CLIENT_EVENTS.hostAdvanceReveal]: hostActionSchema,
  [CLIENT_EVENTS.gameNextRound]: hostActionSchema,
  [CLIENT_EVENTS.gameEnd]: hostActionSchema,
} as const;

export type RoomCreatePayload = z.infer<typeof roomCreateSchema>;
export type RoomJoinPayload = z.infer<typeof roomJoinSchema>;
export type RoomReconnectPayload = z.infer<typeof roomReconnectSchema>;
export type WishSubmitPayload = z.infer<typeof wishSubmitSchema>;
export type ConsequenceSubmitPayload = z.infer<typeof consequenceSubmitSchema>;
export type VoteSubmitPayload = z.infer<typeof voteSubmitSchema>;
export type SettingsPatch = z.infer<typeof settingsPatchSchema>;
